<template>
    <div
        class="navbar-start">
        <top-menu-item
            v-for="item in menuItems"
            :key="item.text"
            v-require-any-role="item.permissions"
            :item="item" />
        <div
            v-show="favourites.length > 0"
            class="navbar-item has-dropdown is-size-5">
            <a
                ref="favouritesHeader"
                class="navbar-link"
                role="button">
                <span
                    class="icon fav-star-active"
                    title="My favourites">
                    <i class="mdi mdi-star" />
                </span>
            </a>
            <top-sub-menu
                ref="favoritesSubMenu"
                :items="favourites" />
        </div>
    </div>
</template>

<script>
    import * as signalR from '@microsoft/signalr';
    import { requireAnyPermission } from '../directives/requirePermission';
    import favouriteService from '../services/favouriteService.ts';
    import { getDynamicMenuItems } from '../helpers/api';
    import TopMenuItem from './TopMenuItem.vue';
    import TopSubMenu from './TopSubMenu.vue';
    import { flip, offset, shift } from '@floating-ui/vue';

    export const defaultMenuItems = [
        {
            text: 'Home',
            link: '/',
            permissions: ['IsAuthenticatedUser']
        },
        {
            text: 'Libraries',
            link: '/Library',
            permissions: ['IsAuthenticatedUser'],
            subItems: [
                {
                    text: 'Library list',
                    link: '/Library',
                    permissions: ['IsAuthenticatedUser']
                },
                {
                    text: 'Library explorer',
                    link: '/Library/library-network',
                    permissions: ['IsAuthenticatedUser']
                },
                {
                    text: 'SQL query',
                    link: '/Sql',
                    permissions: ['IsAuthenticatedUser']
                }
            ]
        },
        {
            text: 'Releases',
            link: '/Release',
            permissions: ['IsAuthenticatedUser'],
            notifications: null,
            subItems: [
                {
                    text: 'All releases',
                    link: '/Release/',
                    permissions: ['IsAuthenticatedUser']
                },
                {
                    text: 'My releases',
                    link: '/Release/MyReleases',
                    permissions: ['IsAdministrator', 'CanEditRelease'],
                    notifications: null
                },
                {
                    text: 'Excel import',
                    link: '/CodeSetImport',
                    permissions: ['IsAdministrator', 'CanEditRelease'],
                    hasSeparator: true
                },
                {
                    text: 'Shipping (beta)',
                    link: '/Shipping',
                    permissions: ['IsAuthenticatedUser']
                }
            ]
        },
        {
            text: 'Function',
            link: '/Function',
            permissions: ['IsAuthenticatedUser'],
            subItems: [
                {
                    text: 'Cable codes',
                    link: '/CableEdit',
                    permissions: ['IsAuthenticatedUser']
                },
                {
                    text: 'Documentation',
                    link: '/Documentation',
                    permissions: ['IsAuthenticatedUser']
                },
                {
                    text: 'Electric component type',
                    link: '/EleComponentType',
                    permissions: ['IsAuthenticatedUser']
                },
                {
                    text: 'Ens Doc format',
                    link: '/DocumentNumberingFormat',
                    permissions: ['IsAuthenticatedUser']
                },
                {
                    text: 'Ens number validation',
                    link: '/EnsNumber',
                    permissions: ['IsAuthenticatedUser']
                },
                {
                    text: 'Ens Tag format',
                    link: '/TagFormat',
                    permissions: ['IsAuthenticatedUser']
                },
                {
                    text: 'Facility and Project UI',
                    link: '/FacilityAndProject',
                    permissions: ['IsAuthenticatedUser']
                },
                {
                    text: 'Piping class and size',
                    link: '/PipeClassEditor',
                    permissions: ['IsAuthenticatedUser']
                },
                {
                    text: 'Project, Contractor and Purchase order UI',
                    link: '/LCI/Projects',
                    permissions: ['IsAuthenticatedUser']
                },
                {
                    text: 'Schema and Mapping',
                    link: '/Schema',
                    permissions: ['IsAuthenticatedUser']
                },
                {
                    text: 'TR document revision (beta)',
                    link: '/TRDocument',
                    permissions: ['IsAuthenticatedUser']
                },
                {
                    text: 'Unit of Measure',
                    link: '/UoM',
                    permissions: ['IsAuthenticatedUser']
                }
            ]
        },
        {
            text: 'Reports',
            link: '/Report',
            permissions: ['IsAuthenticatedUser'],
            subItems: [
                {
                    text: 'Export TR3111 code tables',
                    link: '/CodeTables?group=TR3111',
                    permissions: ['IsAuthenticatedUser']
                },
                {
                    text: 'Library code count',
                    link: '/Report/LibraryCodeCount',
                    permissions: ['IsAuthenticatedUser']
                },
                {
                    text: 'Library scope distribution',
                    link: '/Report/LibraryScopeDistribution',
                    permissions: ['IsAuthenticatedUser']
                },
                {
                    text: 'Library statistics (STID sync)',
                    link: '/Report/LibraryStatistics',
                    permissions: ['IsAuthenticatedUser']
                },
                {
                    text: 'MasterTagType and MasterDocType mapping',
                    link: '/Report/MasterMapping',
                    permissions: ['IsAuthenticatedUser']
                },
                {
                    text: 'Query-based reports',
                    link: '/Report/Query',
                    permissions: ['IsAuthenticatedUser']
                },
                {
                    text: 'Schedules report',
                    link: '/Report/Schedules',
                    permissions: ['IsAuthenticatedUser']
                },
                {
                    text: 'Usage statistics',
                    link: '/Report/Stats',
                    permissions: ['IsAuthenticatedUser']
                }

            ]
        },
        {
            text: 'Configuration',
            link: '/Config',
            permissions: ['IsAuthenticatedUser'],
            subItems: [
                {
                    text: 'Access groups',
                    link: '/Config/AccessGroups',
                    permissions: ['IsAdministrator']
                },
                {
                    text: 'Access policy',
                    link: '/Config/AccessPolicy',
                    permissions: ['IsAdministrator']
                },
                {
                    text: 'Admin commands',
                    link: '/Config/AdminCommands',
                    permissions: ['IsAdministrator']
                },
                {
                    text: 'Aliasing',
                    link: '/Config/Aliasing',
                    permissions: ['IsAdministrator']
                },
                {
                    text: 'Applications',
                    link: '/Config/Application',
                    permissions: ['IsAuthenticatedUser']
                },
                {
                    text: 'Attachment search',
                    link: '/Config/AttachmentSearch',
                    permissions: ['IsAuthenticatedUser']
                },
                {
                    text: 'Changelog admin',
                    link: '/Config/ChangeLogAdmin',
                    permissions: ['IsAdministrator']
                },
                {
                    text: 'Disabled features',
                    link: '/Config/DisabledFeatures',
                    permissions: ['IsAdministrator']
                },
                {
                    text: 'Library groups',
                    link: '/Config/Tags',
                    permissions: ['IsAdministrator', 'CanEditLibrary']
                },
                {
                    text: 'Monitoring',
                    link: '/Config/Monitoring',
                    permissions: ['IsAdministrator']
                },
                {
                    text: 'Publisher',
                    link: '/Config/Publisher',
                    permissions: ['CanEditRelease']
                },
                {
                    text: 'Schedules',
                    link: '/Config/Schedules',
                    permissions: ['IsAuthenticatedUser']
                },
                {
                    text: 'UI design system',
                    link: '/Config/UI',
                    permissions: ['IsAdministrator']
                },
                {
                    text: 'Webhooks',
                    link: '/Config/Webhooks',
                    permissions: ['IsAdministrator']
                }
            ]
        },
        {
            text: 'Help',
            link: '/Help/',
            permissions: ['IsAuthenticatedUser']
        }
    ];

    export default {
        directives: {
            'require-any-role': requireAnyPermission
        },
        components: {
            TopSubMenu,
            TopMenuItem
        },
        data() {
            return {
                menuItems: defaultMenuItems,
                favourites: []
            };
        },
        created() {
            this.connection = new signalR.HubConnectionBuilder()
                .withUrl(window.config.signalRUrlNotification, {
                    accessTokenFactory: async () =>
                        await window.authService.acquireToken()
                })
                .configureLogging(signalR.LogLevel.None)
                .build();
        },
        async mounted() {
            const self = this;
            const reference = this.$refs.favouritesHeader;
            const menu = this.$refs.favoritesSubMenu;
            menu.makeFloating(reference, reference.parentElement, {
                placement: 'bottom-start',
                middleware: [offset({ mainAxis: -2, crossAxis: -3 }), flip(), shift()]
            });

            await this.loadFavourites();
            window.headerMailbox.on('update-favourites', async function() {
                await self.loadFavourites();
            });

            await this.loadDynamicMenu();

            this.connection.on('releaseNotification', count => {
                const item = this.menuItems.find(i => i.text === 'Releases');
                item.notifications = count;

                const subItem = item.subItems.find(x => x.text === 'My Releases');
                subItem.notifications = count;
            });
            await this.connection.start();
        },
        beforeUnmount() {
            this.connection.stop();
        },
        methods: {
            async loadFavourites() {
                const favourites = await favouriteService.get(this);
                this.favourites = favourites.map(fav => this.mapFavouriteToMenuItem(fav));
            },
            async loadDynamicMenu() {
                const dynamicItems = await getDynamicMenuItems(this);
                const mappedDynamicItems = dynamicItems.map(x => this.mapToMenuItem(x));

                const helpItemIndex = this.menuItems.findLastIndex(x => x.text === 'Help');
                const dynamicItemsStartIndex = helpItemIndex !== -1 ? helpItemIndex : this.menuItems.length;
                this.menuItems.splice(dynamicItemsStartIndex, 0, ...mappedDynamicItems);
            },
            mapToMenuItem(dynamicMenuItem) {
                return {
                    text: dynamicMenuItem.name,
                    link: dynamicMenuItem.path,
                    permissions: ['IsAuthenticatedUser'],
                    highlight: dynamicMenuItem.isHighlighted,
                    subItems: dynamicMenuItem.childItems.map(x => this.mapToMenuItem(x)),
                    isDynamic: true
                };
            },
            mapFavouriteToMenuItem(favourite) {
                return {
                    text: favourite.title,
                    link: favourite.url,
                    permissions: ['IsAuthenticatedUser']
                };
            }
        }
    };
</script>
